/**
 * The per-outlet routing switch (02-architecture.md "Outlet routing switch";
 * 09-migration.md "The gateway"; 05-api.md "Admin": `PUT /v1/admin/outlets/{id}/routing`).
 *
 * `platform.outlet_routing` says, per outlet, whether its traffic goes to Laravel
 * (`legacy`) or to v3, with per-module overrides during partial moves. apps/gateway
 * reads it (as qrkey_system) and caches it in Redis; the `routing.setOutletRouting`
 * command changes it and emits `outlet.routing_changed`, whose worker subscriber
 * refreshes the cache and tells every gateway to drop its in-process copy.
 *
 * The cache protocol (key names, value shapes, the compare-and-set script and the
 * invalidation channel) is shared by the writer (apps/api's worker) and the reader
 * (apps/gateway), so it lives here.
 */
import { z } from 'zod';
import { isoTimestampSchema } from './common.js';
import { defineEvent } from './events.js';
import { uuidSchema } from './ids.js';
import { connectorKeySchema } from './integrations.js';

export const ROUTING_TARGETS = ['legacy', 'v3'] as const;
export type RoutingTarget = (typeof ROUTING_TARGETS)[number];
export const routingTargetSchema = z.enum(ROUTING_TARGETS);

/**
 * The modules a request can belong to (CLAUDE.md "What v3.0 is"): the keys of
 * `module_overrides` and the `module` of every gateway route.
 */
export const ROUTING_MODULES = [
  'org',
  'iam',
  'menu',
  'pricing',
  'orders',
  'kitchen',
  'payments',
  'storefront',
  'reports',
  'inventory',
  'bar',
  'accounting',
  'aggregators',
  'delivery',
  'customers',
] as const;
export type RoutingModule = (typeof ROUTING_MODULES)[number];
export const routingModuleSchema = z.enum(ROUTING_MODULES);

/** `{"reports":"legacy"}`: a module that goes elsewhere than the outlet's target. */
export const moduleOverridesSchema = z.partialRecord(routingModuleSchema, routingTargetSchema);
export type ModuleOverrides = z.output<typeof moduleOverridesSchema>;

/** A legacy (MySQL) id: `entity_id` of the outlet. Positive, within JS's safe integers. */
export const legacyEntityIdSchema = z.int().min(1).max(Number.MAX_SAFE_INTEGER);

/**
 * A partner's id for the outlet's store in a webhook payload (WeraFood merchant id,
 * Razorpay account id, …). Kept short and printable: it is a Redis key part.
 */
export const legacyStoreIdSchema = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[\x21-\x7e]+$/, { message: 'Expected printable ASCII without spaces' });

/**
 * One legacy partner webhook identity of the outlet: deliveries on the partner's
 * legacy URL whose payload names `storeId` belong to this outlet and, while the
 * outlet (module) is on v3, are forwarded to `/hooks/{connectorKey}/{installId}`.
 */
export const webhookStoreSchema = z.object({
  connectorKey: connectorKeySchema,
  storeId: legacyStoreIdSchema,
  installId: uuidSchema,
});
export type WebhookStore = z.output<typeof webhookStoreSchema>;

export const webhookStoreRefSchema = webhookStoreSchema.pick({
  connectorKey: true,
  storeId: true,
});
export type WebhookStoreRef = z.output<typeof webhookStoreRefSchema>;

/** `{id}` is the outlet (not `outletId`: an admin route is not scoped to the caller's outlets). */
export const outletRoutingParamsSchema = z.object({ id: uuidSchema });

/** `PUT /v1/admin/outlets/{outletId}/routing`. */
export const setOutletRoutingSchema = z.object({
  target: routingTargetSchema,
  moduleOverrides: moduleOverridesSchema.default({}),
  /**
   * The outlet's Laravel `entity_id`. Defaults to the outlet's own
   * `legacy_entity_id`; required when the outlet has none. Fixed once set.
   */
  legacyEntityId: legacyEntityIdSchema.optional(),
  /** Replaces the outlet's legacy webhook stores when given; unchanged when omitted. */
  webhookStores: z.array(webhookStoreSchema).max(20).optional(),
  /** Why (shown in the audit log). */
  reason: z.string().trim().min(1).max(200).optional(),
});
export type SetOutletRouting = z.output<typeof setOutletRoutingSchema>;

export const outletRoutingSchema = z.object({
  outletId: uuidSchema,
  legacyEntityId: legacyEntityIdSchema,
  target: routingTargetSchema,
  moduleOverrides: moduleOverridesSchema,
  webhookStores: z.array(webhookStoreSchema),
  version: z.int().min(1),
  changedBy: uuidSchema.nullable(),
  changedAt: isoTimestampSchema,
});
export type OutletRoutingView = z.output<typeof outletRoutingSchema>;

/** Where a request for `module` of an outlet with this routing goes. No routing = legacy. */
export function routingTargetFor(
  routing: { readonly target: RoutingTarget; readonly moduleOverrides: ModuleOverrides } | null,
  module: RoutingModule,
): RoutingTarget {
  if (routing === null) return 'legacy';
  return routing.moduleOverrides[module] ?? routing.target;
}

export const outletRoutingChangedV1 = defineEvent(
  'outlet.routing_changed',
  1,
  z.object({
    outletId: uuidSchema,
    legacyEntityId: legacyEntityIdSchema,
    target: routingTargetSchema,
    moduleOverrides: moduleOverridesSchema,
    version: z.int().min(1),
    /** Null when the outlet had no routing row (it was on legacy by default). */
    previousTarget: routingTargetSchema.nullable(),
    webhookStores: z.array(webhookStoreSchema),
    /** Stores the outlet no longer has (their cache entries are dropped). */
    removedWebhookStores: z.array(webhookStoreRefSchema),
  }),
);

export const ROUTING_EVENTS = [outletRoutingChangedV1] as const;

// ---------------------------------------------------------------------------
// The gateway's Redis cache (written by the worker's `routing.refresh_gateway_cache`
// subscriber and by gateways filling misses from Postgres).

/**
 * How long a cached routing decision lives in Redis. Bounds how stale a gateway can
 * be if the invalidation never arrives (worker down): a change takes effect within
 * this plus the gateway's in-process TTL (GATEWAY_MEMORY_CACHE_TTL_MS).
 */
export const ROUTING_CACHE_TTL_MS = 60_000;

/** An outlet's routing as cached: `outlet_routing` minus the audit columns. */
export const cachedRoutingSchema = z.object({
  outletId: uuidSchema,
  companyId: uuidSchema,
  legacyEntityId: legacyEntityIdSchema,
  target: routingTargetSchema,
  moduleOverrides: moduleOverridesSchema,
  version: z.int().min(1),
});
export type CachedRouting = z.output<typeof cachedRoutingSchema>;

/**
 * A cache entry for an outlet or a legacy entity. `routing: null` = no row (the
 * outlet is on legacy by default), cached with version 0 so any real row wins.
 */
export const routingCacheEntrySchema = z.object({
  version: z.int().min(0),
  routing: cachedRoutingSchema.nullable(),
});
export type RoutingCacheEntry = z.output<typeof routingCacheEntrySchema>;

/** A cache entry for a legacy webhook store id. `store: null` = unknown store (legacy). */
export const storeCacheEntrySchema = z.object({
  store: z.object({ outletId: uuidSchema, installId: uuidSchema }).nullable(),
});
export type StoreCacheEntry = z.output<typeof storeCacheEntrySchema>;

/** What a gateway drops from its in-process cache when told so. */
export const routingInvalidationSchema = z.object({
  outletId: uuidSchema.optional(),
  legacyEntityId: legacyEntityIdSchema.optional(),
  stores: z.array(webhookStoreRefSchema).default([]),
});
export type RoutingInvalidation = z.output<typeof routingInvalidationSchema>;

/** Redis key names under the deployment's namespace (BULLMQ_PREFIX). */
export const routingCacheKeys = {
  outlet: (prefix: string, outletId: string) => `${prefix}:gateway:routing:outlet:${outletId}`,
  entity: (prefix: string, legacyEntityId: number) =>
    `${prefix}:gateway:routing:entity:${legacyEntityId}`,
  store: (prefix: string, connectorKey: string, storeId: string) =>
    `${prefix}:gateway:store:${connectorKey}:${storeId}`,
  /** Pub/sub channel of RoutingInvalidation messages. */
  channel: (prefix: string) => `${prefix}:gateway:invalidate`,
} as const;

/**
 * Compare-and-set for routing entries: KEYS[1] = key, ARGV[1] = the entry (JSON),
 * ARGV[2] = its version, ARGV[3] = TTL in ms. Writes unless the key holds a NEWER
 * version, so a gateway that read Postgres just before a flip committed can never
 * overwrite the flip's entry with the old routing. Returns 1 when written.
 */
export const ROUTING_CACHE_SET_SCRIPT = `
local current = redis.call('GET', KEYS[1])
if current then
  local ok, entry = pcall(cjson.decode, current)
  if ok and type(entry) == 'table' and tonumber(entry['version']) and tonumber(entry['version']) > tonumber(ARGV[2]) then
    return 0
  end
end
redis.call('SET', KEYS[1], ARGV[1], 'PX', ARGV[3])
return 1
`;
